import styled from '@emotion/styled';
import { css } from '@emotion/react';

export const HelperText = styled.span`
    display: block;
    margin-top: 4px;
    ${({ theme }) => theme.savory.typography['Caption/Regular/Caption1']};
    color: ${({ theme }) => theme.savory.colors.neutral[500]};
`;

export const AlertText = styled.span`
    display: block;
    margin-top: 4px;
    ${({ theme }) => theme.savory.typography['Caption/Regular/Caption1']};
    color: ${({ theme }) => theme.savory.colors.warning[700]};
`;

export const SectionLabel = styled.label<{ required?: boolean }>`
    display: block;
    margin-top: ${({ theme }) => theme.savory.spacing[2]};
    margin-bottom: ${({ theme }) => theme.savory.spacing[1]};
    ${({ theme }) => theme.savory.typography['Body/Bold/Text3']};
    color: ${({ theme }) => theme.savory.colors.neutral[900]};

    ${({ required, theme }) =>
        required &&
        css`
            &::after {
                content: ' *';
                color: ${theme.savory.colors.destructive[500]};
            }
        `}
`;

export const RadioCardGroup = styled.div`
    display: flex;
    gap: 12px;
`;

export const RadioCard = styled.div<{ selected?: boolean; disabled?: boolean }>`
    flex: 1;
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.savory.spacing[1]};
    padding: 12px;
    border: 1px solid
        ${({ theme, selected }) => (selected ? theme.savory.colors.success[500] : theme.savory.colors.neutral[200])};
    border-radius: 8px;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    background-color: ${({ theme, selected }) => (selected ? `${theme.savory.colors.success[500]}10` : 'transparent')};
    transition: border-color 0.15s ease;

    &:hover {
        ${({ disabled, theme }) =>
            !disabled &&
            css`
                border-color: ${theme.savory.colors.success[500]};
            `}
    }
`;

export const RadioCardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const RadioCardTitle = styled.span<{ selected?: boolean }>`
    ${({ theme }) => theme.savory.typography['Body/Bold/Text3']};
    color: ${({ theme, selected }) => (selected ? theme.savory.colors.success[500] : theme.savory.colors.neutral[900])};
`;

export const RadioCardDescription = styled.span`
    ${({ theme }) => theme.savory.typography['Caption/Regular/Caption1']};
    color: ${({ theme }) => theme.savory.colors.neutral[500]};
`;

export const RadioDot = styled.div<{ selected?: boolean }>`
    width: 18px;
    height: 18px;
    min-width: 18px;
    border-radius: 50%;
    border: 2px solid
        ${({ theme, selected }) => (selected ? theme.savory.colors.success[500] : theme.savory.colors.neutral[200])};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;

    &::after {
        content: '';
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: ${({ theme, selected }) => (selected ? theme.savory.colors.success[500] : 'transparent')};
    }
`;

export const ChannelCheck = styled.div`
    width: 20px;
    height: 20px;
    min-width: 20px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.savory.colors.success[500]};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
    color: ${({ theme }) => theme.savory.colors.neutral[50]};
    ${({ theme }) => theme.savory.typography['Caption/Bold/Caption1']};
`;

export const DisabledInfo = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    ${({ theme }) => theme.savory.typography['Caption/Regular/Caption1']};
    color: ${({ theme }) => theme.savory.colors.neutral[500]};
    margin-top: 4px;
`;

export const ImageSection = styled.div`
    margin-top: ${({ theme }) => theme.savory.spacing[1]};
    border: 1px solid ${({ theme }) => theme.savory.colors.neutral[200]};
    border-radius: 8px;
    width: 100%;

    /* Make the ImageInput image container full width with banner ratio */
    > div > div:first-of-type {
        width: 100%;
        min-height: 180px;
        height: 180px;
        border-radius: 0;
        box-shadow: none;
        flex-shrink: 0;

        > img {
            object-fit: cover;
        }
    }

    /* Hide icon buttons visually but keep them in the flow for click handling */
    > div > div:first-of-type > div:last-child {
        opacity: 0;
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        > button {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            opacity: 0;
        }
    }
`;

export const ImageDescriptionBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${({ theme }) => theme.savory.spacing[1]} 12px;
    background-color: ${({ theme }) => theme.savory.colors.neutral[50]};
    border-top: 1px solid ${({ theme }) => theme.savory.colors.neutral[200]};
`;

export const ImageDescriptionText = styled.span`
    ${({ theme }) => theme.savory.typography['Caption/Regular/Caption1']};
    color: ${({ theme }) => theme.savory.colors.neutral[700]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
`;

export const ReplaceButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    ${({ theme }) => theme.savory.typography['Body/Bold/Text3']};
    color: ${({ theme }) => theme.savory.colors.neutral[900]};
    background-color: #ffffff;
    border: 1px solid ${({ theme }) => theme.savory.colors.neutral[200]};
    border-radius: 6px;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: ${({ theme }) => theme.savory.colors.neutral[100]};
    }
`;

export const ImageSpecs = styled.span`
    display: block;
    text-align: center;
    ${({ theme }) => theme.savory.typography['Caption/Regular/Caption1']};
    color: ${({ theme }) => theme.savory.colors.neutral[500]};
    margin-top: ${({ theme }) => theme.savory.spacing[1]};
`;

export const SearchInput = styled.input`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${({ theme }) => theme.savory.colors.neutral[200]};
    border-radius: 4px;
    ${({ theme }) => theme.savory.typography['Body/Regular/Text3']};
    outline: none;

    &:focus {
        border-color: ${({ theme }) => theme.savory.colors.primary[500]};
    }
`;

export const SelectedItem = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.savory.spacing[1]};
    padding: 10px 12px;
    border: 1px solid ${({ theme }) => theme.savory.colors.success[500]};
    border-radius: 4px;
    background-color: ${({ theme }) => `${theme.savory.colors.success[500]}10`};
    cursor: pointer;

    &:hover {
        background-color: ${({ theme }) => theme.savory.colors.neutral[50]};
    }
`;

export const ProductList = styled.div`
    border: 1px solid ${({ theme }) => theme.savory.colors.neutral[200]};
    border-radius: 4px;
    min-height: 200px;
    max-height: 320px;
    overflow-y: auto;
    margin-top: ${({ theme }) => theme.savory.spacing[1]};
`;

export const ProductItem = styled.div<{ selected?: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    cursor: pointer;
    border-bottom: 1px solid ${({ theme }) => theme.savory.colors.neutral[100]};
    background-color: ${({ selected, theme }) => (selected ? theme.savory.colors.neutral[50] : 'transparent')};

    &:last-of-type {
        border-bottom: none;
    }

    &:hover {
        background-color: ${({ theme }) => theme.savory.colors.neutral[50]};
    }
`;

export const ProductThumb = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 4px;
    object-fit: cover;
    flex-shrink: 0;
`;

export const ProductThumbPlaceholder = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.savory.colors.neutral[100]};
    flex-shrink: 0;
`;

export const ProductName = styled.span`
    ${({ theme }) => theme.savory.typography['Body/Regular/Text3']};
    color: ${({ theme }) => theme.savory.colors.neutral[900]};
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
`;

export const ProductMeta = styled.span`
    ${({ theme }) => theme.savory.typography['Caption/Regular/Caption1']};
    color: ${({ theme }) => theme.savory.colors.neutral[500]};
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;

    &::before {
        content: '|';
        color: ${({ theme }) => theme.savory.colors.neutral[200]};
        margin-right: 4px;
    }
`;

export const ProductMetaBold = styled.span`
    ${({ theme }) => theme.savory.typography['Caption/Bold/Caption1']};
    color: ${({ theme }) => theme.savory.colors.neutral[700]};
`;
